import { Loader2, RotateCcw } from 'lucide-react';
import { useState } from 'react';

import {
  isAnySlotSeeding,
  ownsDemoCopies,
  useDemoRosterQuery,
  useReseedDemoPatientsMutation,
} from '@/entities/sandbox-demo';
import { cn } from '@/lib/utils';
import { clearAllHints } from '@/shared/stores';
import { Button } from '@/shared/ui/shadcn/button';

import { ResetAllDemoPatientsDialog } from './ResetAllDemoPatientsDialog';

/**
 * Reseeds every committed fixture in one request: the server restores ready
 * and failed copies, creates absent ones, and leaves seeding ones to the job
 * that owns them.
 *
 * Hints are cleared once the server accepts the reseed. That waits on the
 * `mutateAsync` promise rather than a per-call `onSuccess`, which is dropped
 * if the dashboard unmounts first. A failure is already toasted and logged by
 * the global `MutationCache` handler, so it is only swallowed here.
 */
export function ResetAllDemoPatientsControl() {
  const { data: roster } = useDemoRosterQuery();
  const reseed = useReseedDemoPatientsMutation();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (roster === undefined || !ownsDemoCopies(roster)) {
    return null;
  }

  const slugs = roster.slots.map((slot) => slot.slug);
  const isResetting = reseed.isPending || isAnySlotSeeding(roster);

  const handleConfirm = () => {
    setDialogOpen(false);
    void reseed.mutateAsync(slugs).then(
      () => clearAllHints(),
      () => undefined
    );
  };

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        disabled={isResetting}
        aria-busy={isResetting}
        onClick={() => setDialogOpen(true)}
        title="Restore every demo patient to its committed case and show the onboarding tips again"
        className={cn(
          'rounded-full border border-white/15 bg-white/8 text-white/80 backdrop-blur-xl',
          'hover:bg-white/15 hover:text-white'
        )}
      >
        {isResetting ? (
          <>
            <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
            Resetting…
          </>
        ) : (
          <>
            <RotateCcw className="size-3.5" aria-hidden="true" />
            Reset all
          </>
        )}
      </Button>
      <ResetAllDemoPatientsDialog
        open={dialogOpen}
        patientCount={slugs.length}
        isPending={reseed.isPending}
        onConfirm={handleConfirm}
        onOpenChange={setDialogOpen}
      />
    </>
  );
}
